import { DEFAULT_THEME, THEME_STORAGE_KEY, type ThemeMode } from './theme.constants';

type Listener = () => void;

const listeners = new Set<Listener>();

function readDocumentTheme(): ThemeMode {
    if (typeof document === 'undefined') return DEFAULT_THEME;
    return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

/**
 * The DOM is the single source of truth: the pre-paint script in `<head>` has
 * already written `data-theme`, so reading it back keeps the first client
 * snapshot identical to what is on screen.
 */
let currentTheme: ThemeMode = readDocumentTheme();

export function subscribeTheme(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getThemeSnapshot(): ThemeMode {
    return currentTheme;
}

/** SSR renders the default; the store corrects it on the first client read. */
export function getThemeServerSnapshot(): ThemeMode {
    return DEFAULT_THEME;
}

export function setTheme(theme: ThemeMode): void {
    currentTheme = theme;
    document.documentElement.dataset.theme = theme;

    try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // A blocked storage (private mode, blanket cookie rejection) only costs
        // persistence across reloads — the toggle itself keeps working.
    }

    listeners.forEach((listener) => listener());
}

export function toggleTheme(): void {
    setTheme(currentTheme === 'dark' ? 'light' : 'dark');
}
